<script setup lang="ts">
import type { MenuProps } from 'ant-design-vue';
import { ref, computed, onMounted, watch } from 'vue';

const emit = defineEmits<{
    (e: 'changeEvent', data?: any): void,
}>()

const props = defineProps({
    inputName: {
        type: String,
        default: 'text',
    },
    placeholder: {
        type: String,
        default: '',
        required: false,
    },
    initialValue: {
        type: [String, Number],
        default: null,
        required: false,
    },
    type: {
        type: String as PropType<'year' | 'month' | 'day'>,
        default: 'year',
        required: false,
    },
    dateType: {
        type: String as PropType<'past' | 'future'>,
        default: 'past',
        required: false,
    },
    rangeYears: {
        type: Number,
        default: 100,
        required: false,
    },
    class: {
        type: String,
        required: false,
    },
    wrapperClass: {
        type: String,
        required: false,
    },
    icon: {
        type: String,
        default: '',
        required: false,
    },
    submitted: {
        type: Boolean,
        default: false,
        required: false,
    },
    hasError: {
        type: Boolean,
        default: false,
        required: false,
    }
});

// Compute class object with error styling
const classObject = computed(() => ({
    'rounded-[1rem] border-[lightGray] border-solid border-[1px] flex px-[1.2rem] items-center': true,
    'border-[#ff4d4f] border-[1px]': (props.submitted && (errorMessage.value || props.hasError)),
}));

// Get route to access query parameters
const route = useRoute();

// Get field information
const { value, setValue, errorMessage } = useField(props.inputName);

// Create reactive reference for displayed value
const selectedValue = ref<string | null>(null);

// Generate array of options based on type
const options = computed(() => {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth() + 1;
    
    if (props.type === 'year') {
        // For years
        const years = [];
        if (props.dateType === 'past') {
            // For birthdays - past years
            for (let i = 0; i < props.rangeYears; i++) {
                years.push(currentYear - i);
            }
            return years.sort((a, b) => b - a); // Sort descending for past years
        } else {
            // For credit card expiry - future years
            for (let i = 0; i <= props.rangeYears; i++) {
                years.push(currentYear + i);
            }
            return years;
        }
    } else if (props.type === 'month') {
        // For months
        return Array.from({ length: 12 }, (_, i) => {
            const monthNum = i + 1;
            return {
                value: monthNum < 10 ? `0${monthNum}` : `${monthNum}`,
                label: new Date(2000, i, 1).toLocaleString('default', { month: 'long' })
            };
        });
    } else if (props.type === 'day') {
        // For days
        return Array.from({ length: 31 }, (_, i) => {
            const dayNum = i + 1;
            return dayNum < 10 ? `0${dayNum}` : `${dayNum}`;
        });
    }
    
    return [];
});

// Format the display value based on type
const displayValue = computed(() => {
    if (!selectedValue.value) return props.placeholder;
    
    if (props.type === 'month' && typeof selectedValue.value === 'string') {
        const monthObj = options.value.find(m => m.value === selectedValue.value);
        return monthObj ? monthObj.label : selectedValue.value;
    }
    
    return selectedValue.value;
});

// Watch for changes in the form field value
watch(value, (newValue) => {
    if (newValue !== undefined && newValue !== null && String(newValue) !== selectedValue.value) {
        console.log(`Field ${props.inputName} value changed externally to:`, newValue);
        selectedValue.value = String(newValue);
    }
}, { immediate: true });

// Watch for changes to initialValue prop
watch(() => props.initialValue, (newValue) => {
    if (newValue !== null && newValue !== undefined) {
        console.log(`initialValue for ${props.inputName} changed to:`, newValue);
        const strValue = String(newValue);
        selectedValue.value = strValue;
        setValue(strValue);
    }
}, { immediate: true });

// Initialize form field with value from various sources
onMounted(() => {
    initializeValue();
});

// Extract the initialization logic to a separate function
const initializeValue = () => {
    // Check for query parameter first
    const queryParam = route.query[props.inputName];
    if (queryParam) {
        const paramValue = String(queryParam);
        console.log(`Found ${props.inputName} in URL query:`, paramValue);
        setValue(paramValue);
        selectedValue.value = paramValue;
        return;
    }
    
    // Then try initialValue prop
    if (props.initialValue !== null && props.initialValue !== undefined) {
        setValue(String(props.initialValue));
        selectedValue.value = String(props.initialValue);
        return;
    } 
    
    // Check if field already has a value
    if (value.value !== null && value.value !== undefined) {
        selectedValue.value = String(value.value);
        return;
    }
    
    // Fallback to placeholder if specified
    if (props.placeholder && props.placeholder !== '') {
        setValue(props.placeholder);
        selectedValue.value = props.placeholder;
    }
    
    console.log(`Field ${props.inputName} initialized with:`, value.value);
    console.log(`Selected value initialized as:`, selectedValue.value);
};

// Handle menu item click
const handleMenuClick: MenuProps['onClick'] = (e) => {
    const selected = String(e.key);
    selectedValue.value = selected;
    setValue(selected);
    emit('changeEvent', selected);
    console.log(`Selected ${props.type}:`, selected);
};

// Compute text color class
const textColorClass = computed(() => (selectedValue.value !== null ? 'text-black' : 'text-[darkGray]'));
</script>

<template>
    <div :class="props.wrapperClass">
        <a-dropdown trigger="click">
            <template #overlay>
                <a-menu @click="handleMenuClick" :style="{ maxHeight: '400px', overflow: 'auto' }">
                    <template v-if="props.type === 'month'">
                        <a-menu-item v-for="option in options" :key="option.value">
                            {{ option.label }}
                        </a-menu-item>
                    </template>
                    <template v-else>
                        <a-menu-item v-for="option in options" :key="option">
                            {{ option }}
                        </a-menu-item>
                    </template>
                </a-menu>
            </template>
            <a-button :class="[ 
                'dateDropdown w-full font-bold placeholder-[darkGray] shadow px-[1.1rem]',
                classObject,
                props.class,
                textColorClass
            ]">
                <CustomIcon v-if="props.icon" :name="props.icon" />
                <span class="font-inter max-md:pl-[.5rem] max-md:font-normal text-[1.4rem]">{{ displayValue }}</span>
            </a-button>
        </a-dropdown>
        
        <template v-if="props.submitted && errorMessage">
            <p class="text-[redError] text-[1.4rem] leading-[1.5] mt-[.4rem]">
                {{ errorMessage }}
            </p>
        </template>
    </div>
</template>